<template>
  <div class="space-y-3">
    <div class="relative" v-if="showDivider">
      <div class="absolute inset-0 flex items-center">
        <span class="w-full border-t" />
      </div>
      <div class="relative flex justify-center text-xs uppercase">
        <span class="bg-background px-2 text-muted-foreground">Get the desktop app</span>
      </div>
    </div>

    <div v-else-if="showTitle" class="text-sm font-medium text-center mb-2">
      Download Desktop App
    </div>

    <div :class="layout === 'horizontal' ? 'flex gap-2' : 'flex flex-col gap-2'">
      <Button
        v-for="platform in platforms"
        :key="platform.key"
        variant="outline"
        size="sm"
        :class="[
          layout === 'horizontal' ? 'flex-1' : 'w-full',
          'hover:bg-accent'
        ]"
        @click="download(platform.key)"
      >
        <component :is="platform.icon" class="h-4 w-4 mr-2" />
        {{ platform.label }}
      </Button>
    </div>

    <p v-if="showDescription" class="text-xs text-muted-foreground text-center">
      Native app with better performance and offline support
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, h } from 'vue'
import { Button } from '@/components/ui/button'
import { Apple, Monitor, PenTool } from 'lucide-vue-next'

const props = withDefaults(defineProps<{
  showDivider?: boolean
  showTitle?: boolean
  showDescription?: boolean
  layout?: 'horizontal' | 'vertical'
}>(), {
  showDivider: false,
  showTitle: false,
  showDescription: true,
  layout: 'vertical'
})

// Supabase storage URL for app downloads
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://ambi-api.gen-go.com'
const STORAGE_URL = `${SUPABASE_URL}/storage/v1/object/public/app-updates`

const platforms = [
  {
    key: 'macos',
    label: 'macOS',
    icon: Apple,
    files: {
      arm: 'Veil.into.Worlds_aarch64.dmg',
      intel: 'Veil.into.Worlds_x64.dmg'
    }
  },
  {
    key: 'windows',
    label: 'Windows',
    icon: Monitor,
    file: 'Veil.into.Worlds_x64_en-US.msi'
  },
  {
    key: 'linux',
    label: 'Linux',
    icon: PenTool, // Using PenTool as a stand-in, could use a custom Linux icon
    file: 'Veil.into.Worlds_amd64.AppImage'
  }
]

const detectPlatform = () => {
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('mac')) return 'macos'
  if (ua.includes('win')) return 'windows'
  if (ua.includes('linux')) return 'linux'
  return 'macos'
}

const detectArch = async () => {
  // Check if running on Apple Silicon
  if ('userAgentData' in navigator) {
    try {
      // @ts-ignore
      const uaData = await navigator.userAgentData.getHighEntropyValues(['architecture'])
      if (uaData.architecture === 'arm') return 'arm'
    } catch {
      // Fall through to default
    }
  }
  return 'intel'
}

const download = async (platformKey: string) => {
  const platform = platforms.find(p => p.key === platformKey)
  if (!platform) return

  let file: string

  if (platformKey === 'macos' && 'files' in platform && platform.files) {
    const arch = await detectArch()
    file = platform.files[arch as 'arm' | 'intel']
  } else if ('file' in platform && platform.file) {
    file = platform.file
  } else {
    return
  }

  const url = `${STORAGE_URL}/${file}`
  window.open(url, '_blank')
}
</script>
